import React, { useState, useEffect } from 'react';
import api from '../services/api';
import TransactionProof from './TransactionProof';
import VerificationStatus from './VerificationStatus';
import SignedReceipt from './SignedReceipt';

/**
 * ProofViewer Component
 * Dedicated view for fetching and displaying transaction proofs
 * Shows all user agreements and allows viewing blockchain proof for each
 */
function ProofViewer({ userId }) {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [proofs, setProofs] = useState({});
  const [receipts, setReceipts] = useState({});
  const [loadingProofs, setLoadingProofs] = useState({});
  const [loadingReceipts, setLoadingReceipts] = useState({});
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchAgreements();
  }, [userId]);

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUserAgreements(userId);
      // Sort by creation date, newest first
      const sorted = data.sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setAgreements(sorted);

      // Auto-load proofs and receipts for agreements that already have them
      const initialProofs = {};
      const initialReceipts = {};
      sorted.forEach(agreement => {
        if (agreement.transactionProof) {
          initialProofs[agreement.agreementId] = agreement.transactionProof;
        }
        if (agreement.signedReceipt) {
          initialReceipts[agreement.agreementId] = agreement.signedReceipt;
        }
      });
      setProofs(initialProofs);
      setReceipts(initialReceipts);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProof = async (agreement) => {
    const agreementId = agreement.agreementId;

    // If we already have the proof, don't fetch again
    if (proofs[agreementId]) {
      return;
    }

    try {
      setLoadingProofs(prev => ({ ...prev, [agreementId]: true }));

      // Determine which transaction ID to use for proof lookup
      // Priority: txId (for direct transfers and single operations) > createTxId (for escrow) > agreementId (fallback for old data)
      let txIdToQuery = agreement.txId || agreement.createTxId || agreementId;

      console.log(`Fetching proof for agreement ${agreementId} using txId: ${txIdToQuery}`);

      const proofData = await api.getTransactionProof(txIdToQuery);
      const { signedReceipt, ...proof } = proofData;
      setProofs(prev => ({ ...prev, [agreementId]: proof }));
      if (signedReceipt) {
        setReceipts(prev => ({ ...prev, [agreementId]: signedReceipt }));
      }
    } catch (err) {
      const errorMsg = err.message.includes('not found') || err.message.includes('No rows')
        ? `Unable to fetch proof. This may be an older transaction created before proof tracking was enabled, or the transaction ID format is incompatible.`
        : `Error fetching proof: ${err.message}`;
      alert(errorMsg);
    } finally {
      setLoadingProofs(prev => ({ ...prev, [agreementId]: false }));
    }
  };

  const fetchReceipt = async (agreementId) => {
    if (receipts[agreementId]) return;
    try {
      setLoadingReceipts(prev => ({ ...prev, [agreementId]: true }));
      const receipt = await api.getSignedReceipt(agreementId);
      setReceipts(prev => ({ ...prev, [agreementId]: receipt }));
    } catch (err) {
      alert(`Error fetching receipt: ${err.message}`);
    } finally {
      setLoadingReceipts(prev => ({ ...prev, [agreementId]: false }));
    }
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-pending';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('complete')) return 'status-completed';
    if (statusLower.includes('escrow')) return 'status-escrowed';
    if (statusLower.includes('pending')) return 'status-pending';
    if (statusLower.includes('expire') || statusLower.includes('reject')) return 'status-expired';
    return 'status-pending';
  };

  const getStatusBadge = (status) => {
    if (!status) return 'PENDING';
    return status.toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getUserRole = (agreement) => {
    if (agreement.payerId === userId) return 'Sender';
    if (agreement.payeeId === userId) return 'Beneficiary';
    if (agreement.verifierId === userId) return 'Validator';
    return 'Unknown';
  };

  const canAccept = (agreement) => {
    if (agreement.status !== 'PENDING') return false;
    if (agreement.payeeId === userId && !agreement.acceptances?.payee) return true;
    if (agreement.verifierId === userId && !agreement.acceptances?.verifier) return true;
    return false;
  };

  const canVerify = (agreement) => {
    return agreement.verifierId === userId &&
           agreement.status === 'ESCROWED' &&
           !agreement.isExpired;
  };

  const handleAcceptAgreement = async (agreementId) => {
    if (!window.confirm('Are you sure you want to accept this agreement?')) return;
    try {
      setProcessingId(agreementId);
      await api.acceptAgreement(agreementId, userId);
      alert('Agreement accepted successfully!');
      await fetchAgreements();
    } catch (err) {
      alert(`Error accepting agreement: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleVerifyAgreement = async (agreementId) => {
    if (!window.confirm('Are you sure you want to validate this agreement and release funds to the beneficiary?')) return;
    try {
      setProcessingId(agreementId);
      await api.agreementMet(agreementId, userId);
      alert('Agreement validated! Funds released to beneficiary.');
      await fetchAgreements();
    } catch (err) {
      alert(`Error validating agreement: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="content-card">
        <h2 style={{ color: '#333', marginBottom: '25px' }}>Transaction Proofs</h2>
        <div className="loading">Loading transactions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-card">
        <h2 style={{ color: '#333', marginBottom: '25px' }}>Transaction Proofs</h2>
        <div className="alert alert-error">{error}</div>
        <button onClick={fetchAgreements} className="btn btn-secondary" style={{ marginTop: '15px' }}>
          Retry
        </button>
      </div>
    );
  }

  const instantTransfers = agreements.filter(a => a.type === 'DIRECT_TRANSFER');
  const escrowAgreements = agreements.filter(a => a.type !== 'DIRECT_TRANSFER');

  const renderAgreementCard = (agreement) => {
    const isDirect = agreement.type === 'DIRECT_TRANSFER';
    const accentColor = isDirect ? '#276749' : '#1a365d';
    const accentBg = isDirect ? '#f0fff4' : '#ebf8ff';
    const accentBorder = isDirect ? '#9ae6b4' : '#90cdf4';
    const accentBanner = isDirect
      ? 'linear-gradient(135deg, #276749 0%, #38a169 100%)'
      : 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)';

    return (
      <div key={agreement.agreementId} style={{
        borderRadius: '14px',
        border: `2px solid ${accentBorder}`,
        background: accentBg,
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
      }}>
        {/* Colored top banner */}
        <div style={{
          background: accentBanner,
          padding: '12px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div>
              <div style={{ color: 'white', fontWeight: '700', fontSize: '1rem' }}>
                {isDirect ? 'Instant Transfer' : 'Escrow Agreement'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                {agreement.agreementId.substring(0, 24)}...
              </div>
            </div>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.25)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '700',
            letterSpacing: '0.05em'
          }}>
            {agreement.status || 'PENDING'}
          </div>
        </div>

        {/* Card body */}
        <div style={{ padding: '20px' }}>
          {/* Key details row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
            background: 'white',
            borderRadius: '10px',
            padding: '14px',
            border: `1px solid ${accentBorder}`
          }}>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>From</div>
              <div style={{ fontWeight: '600', color: accentColor }}>{agreement.payerId}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>To</div>
              <div style={{ fontWeight: '600', color: accentColor }}>{agreement.payeeId}</div>
            </div>
            {agreement.verifierId && (
              <div>
                <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Validator</div>
                <div style={{ fontWeight: '600', color: accentColor }}>{agreement.verifierId}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Amount</div>
              <div style={{ fontWeight: '700', fontSize: '1.1rem', color: accentColor }}>
                {agreement.amount ? `${agreement.amount.toLocaleString()} ¢` : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Your Role</div>
              <div style={{ fontWeight: '600', color: accentColor }}>{getUserRole(agreement)}</div>
            </div>
            {agreement.createdAt && (
              <div>
                <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Date</div>
                <div style={{ fontSize: '0.85rem', color: '#444' }}>{formatDate(agreement.createdAt)}</div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {(canAccept(agreement) || canVerify(agreement) || (agreement.status === 'ESCROWED' && agreement.verifierId !== userId)) && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              {canAccept(agreement) && (
                <button
                  onClick={() => handleAcceptAgreement(agreement.agreementId)}
                  className="btn btn-primary"
                  disabled={processingId === agreement.agreementId}
                  style={{ flex: 1, padding: '12px' }}
                >
                  {processingId === agreement.agreementId ? 'Processing...' : 'Accept Agreement'}
                </button>
              )}
              {canVerify(agreement) && (
                <button
                  onClick={() => handleVerifyAgreement(agreement.agreementId)}
                  disabled={processingId === agreement.agreementId}
                  style={{
                    flex: 1, padding: '12px',
                    background: 'linear-gradient(135deg, #276749 0%, #38a169 100%)',
                    color: 'white', border: 'none', borderRadius: '10px',
                    fontSize: '1rem', fontWeight: '700', cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(39, 103, 73, 0.4)'
                  }}
                >
                  {processingId === agreement.agreementId ? 'Processing...' : 'Validate & Release Funds'}
                </button>
              )}
              {agreement.status === 'ESCROWED' && agreement.verifierId !== userId && (
                <div style={{
                  flex: 1, padding: '12px', background: '#fffbeb',
                  border: '1px solid #f6e05e', borderRadius: '10px',
                  fontSize: '0.9rem', color: '#744210', textAlign: 'center'
                }}>
                  Awaiting validation from <strong>{agreement.verifierId}</strong>
                </div>
              )}
            </div>
          )}

          {/* Signed Receipt — always visible button */}
          {!receipts[agreement.agreementId] ? (
            <button
              onClick={() => fetchReceipt(agreement.agreementId)}
              disabled={loadingReceipts[agreement.agreementId]}
              style={{
                width: '100%', padding: '12px',
                background: 'linear-gradient(135deg, #744210 0%, #d4a017 100%)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(212,160,23,0.35)',
                marginBottom: '10px'
              }}
            >
              {loadingReceipts[agreement.agreementId]
                ? <><span className="loading-spinner-small"></span>Generating Receipt...</>
                : 'Get Signed Receipt'}
            </button>
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <SignedReceipt receipt={receipts[agreement.agreementId]} />
            </div>
          )}

          {/* Blockchain Proof */}
          {!proofs[agreement.agreementId] ? (
            <button
              onClick={() => fetchProof(agreement)}
              className="btn btn-primary"
              disabled={loadingProofs[agreement.agreementId]}
              style={{ width: '100%' }}
            >
              {loadingProofs[agreement.agreementId] ? (
                <><span className="loading-spinner-small"></span>Fetching Proof from Blockchain...</>
              ) : (
                'View Blockchain Proof'
              )}
            </button>
          ) : (
            <div style={{ marginTop: '10px' }}>
              <VerificationStatus proof={proofs[agreement.agreementId]} />
              <TransactionProof
                agreementId={agreement.agreementId}
                proof={proofs[agreement.agreementId]}
                compact={false}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="content-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h2 style={{ color: '#333', margin: 0 }}>My Agreements & Transfers</h2>
        <button onClick={fetchAgreements} className="btn btn-secondary">Refresh</button>
      </div>

      {agreements.length === 0 ? (
        <div className="empty-state">
          <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>No Transactions Found</p>
          <p style={{ fontSize: '0.9rem', marginTop: '10px', color: '#666' }}>
            Create an agreement or send Centro to get started.
          </p>
        </div>
      ) : (
        <div>
          {/* Instant Transfers section */}
          {instantTransfers.length > 0 && (
            <div style={{ marginBottom: '30px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '15px', paddingBottom: '10px',
                borderBottom: '3px solid #38a169'
              }}>
                <h3 style={{ color: '#276749', margin: 0, fontSize: '1.1rem' }}>
                  Instant Transfers
                </h3>
                <span style={{
                  background: '#c6f6d5', color: '#22543d',
                  borderRadius: '20px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: '700'
                }}>{instantTransfers.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {instantTransfers.map(renderAgreementCard)}
              </div>
            </div>
          )}

          {/* Escrow Agreements section */}
          {escrowAgreements.length > 0 && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginBottom: '15px', paddingBottom: '10px',
                borderBottom: '3px solid #2563eb'
              }}>
                <h3 style={{ color: '#1a365d', margin: 0, fontSize: '1.1rem' }}>
                  Escrow Agreements
                </h3>
                <span style={{
                  background: '#bee3f8', color: '#1a365d',
                  borderRadius: '20px', padding: '2px 10px', fontSize: '0.8rem', fontWeight: '700'
                }}>{escrowAgreements.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {escrowAgreements.map(renderAgreementCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProofViewer;
